import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import { setupStepOrder } from '../../../@types/Company';
import { addSelectedRow, removeSelectedRow } from '../../../store/CoproReducer';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons/RedirectionCoproButtons.module';
import RedirectionMapButtons from '../../common/RedirectionMapButtons/RedirectionMapButtons.module';
import Spinner from '../../common/Spinner';

interface RnicVigilanceItem {
  nomDusageDeLaCopropriete?: string;
  adresseDeReference?: string;
  numeroDimmatriculation?: string;
  detailsDonneesfinancieresDateag?: string;
  detailsIdentificationNblotshabburcom?: number;
  nombreTotalDeLotsAUsageDhabitationOuDeComme?: number;
  matchedBanNumero?: string;
  matchedBanNomVoie?: string;
  banCodePostal?: string;
  matchedBanNomCom?: string;
  matchedBanIdAdr?: string;
  [key: string]: unknown;
}

interface CoproData {
  NOM: string;
  ADRESSE: string;
  'N° IMMAT': string;
  coproData: RnicVigilanceItem | ApiCoproData;
}

interface NewCoproData extends Omit<CoproData, 'coproData'> {
  coproData: RnicVigilanceItem;
}

interface ApiCoproData {
  id: number;
  name: string;
  batchSize: number;
  remoteId: string;
  addresses: Array<{
    postalCode: string;
    address: string;
    city: string;
  }>;
}

function CoproVigilance() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [newCopros, setNewCopros] = useState<CoproData[]>([]);
  const [lostAttributionCopros, setLostAttributionCopros] = useState<CoproData[]>([]);
  const [unsyncedCopros, setUnsyncedCopros] = useState<CoproData[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedRows = useAppSelector((state) => state.copro.selectedRows[setupStepOrder.indexOf('vigilanceAdditionalCopro')]);

  async function fetchData() {
    setStatus('PENDING');
    try {
      const headers = JWTHeader(jwt);
      const [newCoprosRes, lostCoprosRes, unsyncedCoprosRes] = await Promise.all([
        fetch(`/api/companies/${company.id}/vigilances/copros/new`, headers),
        fetch(`/api/companies/${company.id}/vigilances/copros/lost`, headers),
        fetch(`/api/companies/${company.id}/vigilances/copros/unmatched`, headers),
      ]);

      if (!newCoprosRes.ok || !lostCoprosRes.ok || !unsyncedCoprosRes.ok) {
        throw new Error('One or more API requests failed');
      }

      const [newCoprosData, lostCoprosData, unsyncedCoprosData] = await Promise.all([
        newCoprosRes.json(),
        lostCoprosRes.json(),
        unsyncedCoprosRes.json(),
      ]);

      const formatNewCoprosData = (data: Record<string, RnicVigilanceItem>): CoproData[] => Object.values(data).map((item: RnicVigilanceItem) => ({
        NOM: item.nomDusageDeLaCopropriete,
        ADRESSE: item.adresseDeReference,
        'N° IMMAT': item.numeroDimmatriculation,
        coproData: item,
      }));

      const formatApiCoproData = (data: ApiCoproData[]): CoproData[] => data.map((item) => ({
        NOM: item.name,
        ADRESSE: item.addresses[0] ? `${item.addresses[0].address}, ${item.addresses[0].postalCode} ${item.addresses[0].city}` : 'Adresse non disponible',
        'N° IMMAT': item.remoteId,
        coproData: item,
      }));

      setNewCopros(formatNewCoprosData(newCoprosData));
      setLostAttributionCopros(formatApiCoproData(lostCoprosData));
      setUnsyncedCopros(formatApiCoproData(unsyncedCoprosData));
      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching data :', error);
      setStatus('ERROR');
    }
  }

  useEffect(() => {
    if (company && company.id && jwt) {
      fetchData();
    }
  }, [company, jwt]);

  const exportToExcel = async() => {
    if (newCopros.length === 0 && lostAttributionCopros.length === 0 && unsyncedCopros.length === 0) {
      showToast('warning', 'Aucune donnée à exporter.');
      return;
    }

    // Create workbook
    const workbook = new ExcelJS.Workbook();

    // Helper function to create worksheet data
    const createWorksheetData = (data: CoproData[], title: string) => {
      const worksheet = workbook.addWorksheet(title);

      // Add title
      worksheet.addRow([title]).font = { bold: true, size: 16 };
      worksheet.addRow([]);

      // Add headers
      const headers = ['NOM', 'ADRESSE', 'N° IMMAT'];
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4472C4' },
      };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

      // Add data
      data.forEach((item) => {
        const row = worksheet.addRow([item.NOM, item.ADRESSE, item['N° IMMAT']]);
        row.alignment = { horizontal: 'left', vertical: 'middle' };
      });

      // Auto-fit columns
      worksheet.columns.forEach((column) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 10;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });
        column.width = Math.min(maxLength + 2, 50); // Cap width at 50
      });
    };

    // Create worksheets
    if (newCopros.length > 0) {
      createWorksheetData(newCopros, 'Nouvelles Copropriétés');
    }

    if (lostAttributionCopros.length > 0) {
      createWorksheetData(lostAttributionCopros, 'Copropriétés Perdues');
    }

    if (user && user.roles.includes('ROLE_DEVELOPER') && unsyncedCopros.length > 0) {
      createWorksheetData(unsyncedCopros, 'Copropriétés Non Synchronisées');
    }

    // Generate and save the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `Vigilance_Copropriétés_${new Date().toISOString()}.xlsx`;
    link.click();
  };

  const handleCheckedDataChange = (checked: boolean, index: number) => {
    if (checked) {
      dispatch(addSelectedRow({ step: 'vigilanceAdditionalCopro', row: index }));
    } else {
      dispatch(removeSelectedRow({ step: 'vigilanceAdditionalCopro', row: index }));
    }
  };

  const handleSave = async() => {
    if (loading) { // prevent double call
      return;
    }
    setLoading(true);

    if (!company || !jwt || !company.id || !newCopros) {
      setLoading(false);
      return;
    }

    if (selectedRows.length === 0) {
      setLoading(false);
      return;
    }

    const createCopro = async(copro: NewCoproData) => {
      const payload = {
        name: copro.NOM,
        remoteId: copro['N° IMMAT'],
        type: 1, // copro type as defined in the API
        validatedByUser: true,
        generalMeetingApiDate: copro.coproData.detailsDonneesfinancieresDateag,
        batchSize: copro.coproData.detailsIdentificationNblotshabburcom || copro.coproData.nombreTotalDeLotsAUsageDhabitationOuDeComme,
      };

      const response = await fetch(`/api/companies/${company.id}/copros`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify(payload),
      });

      // Idempotent: already present remoteId (409) — treat as success and skip address create
      if (response.status === 409) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Error creating copro');
      }

      return response.json();
    };

    const createAddress = async(coproId: number, copro: NewCoproData) => {
      // if no matchedBan, return
      if (!copro.coproData.matchedBanNumero || !copro.coproData.matchedBanNomVoie || !copro.coproData.banCodePostal || !copro.coproData.matchedBanNomCom) {
        return;
      }

      const addressPayload = {
        address: `${copro.coproData.matchedBanNumero} ${copro.coproData.matchedBanNomVoie}`,
        postalCode: copro.coproData.banCodePostal,
        city: copro.coproData.matchedBanNomCom,
        remoteId: copro.coproData.matchedBanIdAdr,
      };

      const response = await fetch(`/api/copros/${coproId}/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify(addressPayload),
      });

      if (!response.ok) {
        throw new Error('Error creating address');
      }
    };

    try {
      const createPromises = selectedRows.map(async(index) => {
        const copro = newCopros[index] as NewCoproData;
        const newCopro = await createCopro(copro);
        if (newCopro?.id) {
          await createAddress(newCopro.id, copro);
        }
      });

      await Promise.all(createPromises);

      setLoading(false);
      showToast('success', 'Les copropriétés sélectionnées ont été ajoutées avec succès.');
      await fetchData(); // Refresh the data
    } catch (error) {
      setLoading(false);
      showToast('error', 'Une erreur est survenue lors de l\'ajout des copropriétés.');
      console.error('Error saving data :', error);
    }
  };

  const renderNewCoprosTable = (data: CoproData[], title: string) => (
    <div className="my-10">
      <h2 className="mt-12 mb-4 text-center text-xl text-rgpd-blue-light">{title}</h2>
      <MUITable
        data={data.map((item, index) => ({
          NOM: item.NOM,
          ADRESSE: item.ADRESSE,
          'N° IMMAT': item['N° IMMAT'],
          VALIDE: selectedRows.includes(index),
        }))}
        headers={[
          { title: 'NOM' },
          { title: 'ADRESSE' },
          { title: 'N° IMMAT' },
        ]}
        tableClass="min-w-full bg-white border text-sm"
        sortableColumns={['NOM', 'ADRESSE', 'N° IMMAT']}
        isSearchable
        dataChangeCallback={handleCheckedDataChange}
        disablePagination
      />
      {data.length === 0 && (
        <div className="mx-10 my-10 text-center">
          <Badge mode="success" text="Aucune nouvelle copropriété détectée" />
        </div>
      )}
      {data.length > 0 && (
        <div className="flex w-full flex-row-reverse space-x-reverse">
          <button
            className={classNames(
              'bg-rgpd-green text-white text-lg px-3 py-2 rounded shadow-md mt-6 mb-8 align-center',
              (!data.length) && 'opacity-80 cursor-not-allowed',
            )}
            onClick={handleSave}
            disabled={loading}
          >
            {loading && (
              <>
                <Spinner aria-label="Center-aligned spinner" size="sm" />
                <span className="ml-2">Enregistrement</span>
              </>
            )}
            {!loading && 'Ajouter les copropriétés sélectionnées'}
          </button>
        </div>
      )}
    </div>
  );

  const renderStandardTable = (data: CoproData[], title: string) => (
    <div className="my-10">
      <h2 className="mt-12 mb-4 text-center text-xl text-rgpd-blue-light">{title}</h2>
      <MUITable
        data={data.map((item) => ({
          NOM: (
            <div className="flex items-center">
              <RedirectionCoproButtons coproId={(item.coproData as ApiCoproData).id} />
              {item.NOM}
            </div>
          ),
          ADRESSE: (
            <div className="flex items-center">
              <RedirectionMapButtons address={item.ADRESSE} />
              {item.ADRESSE}
            </div>
          ),
          'N° IMMAT': item['N° IMMAT'],
        }))}
        headers={[
          { title: 'NOM' },
          { title: 'ADRESSE' },
          { title: 'N° IMMAT' },
        ]}
        tableClass="min-w-full bg-white border text-sm"
        sortableColumns={['NOM', 'ADRESSE', 'N° IMMAT']}
        isSearchable
      />
      {data.length === 0 && (
        <div className="mx-10 my-10 text-center">
          <Badge mode="success" text="Aucune copropriété, toutes vos copropriétés sont à jour" />
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col">
      <div className="mb-8 overflow-x-hidden rounded-lg bg-white p-4 shadow-md">
        <div className="mt-4 flex w-full justify-end">
          <button
            onClick={exportToExcel}
            color="success"
            className="mt-2 flex w-full items-center justify-center rounded border border-rgpd-green px-3 py-1 text-rgpd-green transition-colors hover:bg-rgpd-green hover:text-white md:mt-0 md:w-auto"
          >
            <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
            Exporter
          </button>
        </div>
        <div className="mt-4 mb-12 text-lg">
          <p>
            Cet outil vous permet de :
          </p>
          <ul className="mt-2 ml-6 list-disc space-y-2">
            <li>Surveiller les nouvelles copropriétés ajoutées à votre SIREN dans le registre</li>
            <li>Identifier les copropriétés dont vous avez perdu l'attribution dans le registre : cela peut etre signe qu'une copropriété est arrivée à échéance de son mandat ou qu'une erreur à pu survenir lors d'une saisie ou au sein de votre logiciel de gestion</li>
            {user && user.roles.includes('ROLE_DEVELOPER') && (
              <li className="italic">Repérer les copropriétés non synchronisées depuis le dernier import trimestriel : cela permet d'indiquer qu'elles sont suivies moins régulièrement par Déclarations Juridiques</li>
            )}
          </ul>
          <p className="mt-4">
            Utilisez les tableaux ci-dessous pour visualiser et gérer efficacement ces changements importants.
          </p>
        </div>
        {status === 'PENDING' && (
          <div className="my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </div>
        )}
        {status === 'SUCCESS' && (
          <>
            {renderNewCoprosTable(newCopros, 'NOUVELLES COPROPRIÉTÉS')}
            {renderStandardTable(lostAttributionCopros, 'COPROPRIÉTÉS DONT VOUS AVEZ PERDU L\'ATTRIBUTION DANS LE REGISTRE')}
            {user && user.roles.includes('ROLE_DEVELOPER') && renderStandardTable(unsyncedCopros, 'COPROPRIÉTÉS NON SYNCHRONISÉES (seulement mis à jour depuis l\'import trimestriel)')}
          </>
        )}
        {status === 'ERROR' && (
          <div className="my-32 text-center">
            <Badge mode="error" text="Une erreur est survenue lors du chargement des données." />
          </div>
        )}
      </div>
    </div>
  );
}

export default CoproVigilance;
